import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import type { AdminAccount } from '@common/models/account/admin/admin-account.modl'
import { AccountService } from '@common/services/account-service'
import { signOut } from 'firebase/auth'
import { auth } from '../config/firebase'
import { useAuth } from './AuthContext'

type AdminAccountContextType = {
  adminAccount: AdminAccount | null;
  setAdminAccount: (account: AdminAccount | null) => void;
  isLoading: boolean;
  resetAdminAccount: () => void;
};

const AdminAccountContext = createContext<AdminAccountContextType>({
  adminAccount: null,
  setAdminAccount: () => {},
  isLoading: false,
  resetAdminAccount: () => {}
})

// localStorage のキー
const ADMIN_ACCOUNT_STORAGE_KEY = 'adminAccount';

// JSONパース時のDate型復元ヘルパー
const parseAdminAccount = (data: any): AdminAccount | null => {
  if (!data) return null
  return {
    ...data,
    created_at: data.created_at ? new Date(data.created_at) : new Date(0),
    updated_at: data.updated_at ? new Date(data.updated_at) : new Date(0),
    lastLoginAt: data.lastLoginAt ? new Date(data.lastLoginAt) : undefined,
  }
}

export const AdminAccountProvider = ({ children }: { children: ReactNode }) => {
  // 初期化時にローカルストレージから復元
  const [adminAccount, setAdminAccount] = useState<AdminAccount | null>(() => {
    const cached = localStorage.getItem(ADMIN_ACCOUNT_STORAGE_KEY)
    if (cached) {
      try {
        return parseAdminAccount(JSON.parse(cached))
      } catch (error) {
        console.error('Failed to parse cached admin account:', error)
        localStorage.removeItem(ADMIN_ACCOUNT_STORAGE_KEY)
        return null
      }
    }
    return null
  })

  // キャッシュがある場合はローディング完了とする（SWR的な挙動のため）
  const [isLoading, setIsLoading] = useState(() => !adminAccount)
  
  const { user, loading: authLoading } = useAuth()

  const resetAdminAccount = () => {
    setAdminAccount(null)
    setIsLoading(false)
    localStorage.removeItem(ADMIN_ACCOUNT_STORAGE_KEY)
  }

  // ユーザーがログインしたときに、AdminAccountを読み込む（キャッシュがあっても最新化のため実行）
  useEffect(() => {
    const loadAdminAccount = async () => {
      // 認証が読み込み中の場合はスキップ
      if (authLoading) return

      // ユーザーがログインしていない場合
      if (!user) {
        localStorage.removeItem(ADMIN_ACCOUNT_STORAGE_KEY)
        setAdminAccount(null)
        setIsLoading(false)
        return
      }

      try {
        // キャッシュがない場合のみローディングを表示
        // (adminAccount stateは初期化関数でセットされているので、ここではその値が見えるはずだが、
        //  useEffect内での参照はクロージャのタイミングによる。
        //  ただし、isLoadingの初期値制御でカバーしているので、ここでは再設定しない方が安全かも？
        //  いや、もしadminAccountがnullならローディング出すべき)
        setAdminAccount((prev) => {
          if (!prev) setIsLoading(true)
          return prev
        })

        if (!user.uid) {
          console.error('User UID is not available')
          await signOut(auth)
          return
        }

        // ログアウト後の再ログインで Firestore が古いトークンを使うと permission-denied になるため、
        // カスタムクレームを含むトークンを明示的に取得してから読取する
        await user.getIdToken(true)

        const accountService = new AccountService()
        const account = await accountService.getAdminAccount(user.uid)

        if (!account) {
          // ドキュメントが存在しないか削除済みの場合はログアウト
          console.warn('AdminAccount not found or deleted for uid:', user.uid)
          await signOut(auth)
          localStorage.removeItem(ADMIN_ACCOUNT_STORAGE_KEY)
          return
        }

        setAdminAccount(account)
        localStorage.setItem(ADMIN_ACCOUNT_STORAGE_KEY, JSON.stringify(account))
      } catch (error) {
        console.error('Failed to load adminAccount:', error)
        // エラーが発生した場合もログアウト（ただしキャッシュがある場合は維持する選択肢もあるが、整合性のためログアウト推奨）
        // ここでは安全側に倒してログアウト
        await signOut(auth)
        localStorage.removeItem(ADMIN_ACCOUNT_STORAGE_KEY)
      } finally {
        setIsLoading(false)
      }
    }

    loadAdminAccount()
  }, [user?.uid, authLoading])

  // adminAccountが変更されたときにlocalStorageを更新（useEffectでの監視は不要、set時に行う）

  return (
    <AdminAccountContext.Provider value={{ adminAccount, setAdminAccount, isLoading, resetAdminAccount }}>
      {children}
    </AdminAccountContext.Provider>
  );
};

export const useAdminAccount = () => {
  const context = useContext(AdminAccountContext);
  if (!context) {
    throw new Error('useAdminAccount must be used within an AdminAccountProvider');
  }
  return context;
};

